import { Loader } from '@10xLabs/react-web/components/Loader'
import { NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import { Button } from 'components/v2/Button'
import EmptyState from 'components/v2/EmptyState'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { IPaymentSummariesFormValues } from 'features/PaymentsSummary/types'
import { ADD_PAYMENT_SUMMARY_FILES } from 'features/PaymentsSummary/mutations'
import { NewPaymentSummaries } from 'features/PaymentsSummary/components/NewPaymentSummaries'
import { GET_PARTNERS_PAYMENT_SUMMARY } from 'features/PaymentsSummary/queries'
import { createPaymentSummaryFormValues } from 'features/PaymentsSummary/models'
import { PartnersPaymentSummary } from 'gql/generated/graphql'

const messages = defineMessages({
  helmet: {
    id: 'paymentSummary.new.helmet',
    defaultMessage: 'Edit Payment Summary | Nexbus Admin'
  }
})

const EditPaymentSummaryView = (): ReactElement => {
  let content: ReactElement = <></>
  const intl = useIntl()
  const login = useLogin()
  const { paymentSummaryId } = useParams()

  const {
    data: paymentSummaryData,
    loading: paymentSummaryLoading,
    error: paymentSummaryError,
    refetch: refetchPaymentSummary,
    networkStatus: paymentSummaryNetworkStatus
  } = useQuery(GET_PARTNERS_PAYMENT_SUMMARY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      input: {
        id: paymentSummaryId
      }
    }
  })
  const [addPaymentSummariesFiles, { loading, error }] = useMutation(ADD_PAYMENT_SUMMARY_FILES, {
    onCompleted: (): void => {
      setTimeout(() => {
        window.location.reload()
      }, 5000)
    },
    onError: (): void => undefined
  })

  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [intl])

  if (!canWrite(FEATURES.PARTNERS_PAYMENT_SUMMARY, login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.PARTNERS_PAYMENT_SUMMARY} />
  }

  const handleSubmit = async (values: IPaymentSummariesFormValues): Promise<void> => {
    const readyDocuments = values.files.filter((file) => !!(file.file && file.path))

    try {
      await addPaymentSummariesFiles({
        variables: {
          input: {
            summaryID: paymentSummaryId,
            files: readyDocuments.map((file) => ({
              category: file.category,
              filePath: file.path as string
            }))
          }
        }
      })
    } catch (error) {
      // Do nothing
    }
  }

  if (paymentSummaryLoading || paymentSummaryNetworkStatus === NetworkStatus.refetch) {
    content = (
      <FormLayout.Card>
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      </FormLayout.Card>
    )
  } else if (paymentSummaryError) {
    content = (
      <FormLayout.Card>
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="paymentSummary.edit.error.description"
              defaultMessage="There was an error while loading your payment summary"
            />
          </EmptyState.Description>
          <EmptyState.Action>
            <Button
              onClick={(): void => {
                refetchPaymentSummary()
              }}
            >
              <FormattedMessage id="paymentSummary.edit.error.action" defaultMessage="Try again" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      </FormLayout.Card>
    )
  } else {
    const initialValues = createPaymentSummaryFormValues(
      paymentSummaryData?.partnersPaymentSummary as PartnersPaymentSummary
    )
    content = (
      <NewPaymentSummaries
        initialValues={initialValues}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        isEdit
      />
    )
  }
  return (
    <FormLayout.Container>
      <FormLayout.Content isSmallContent>
        <Heading.H3 capitalize>
          <FormattedMessage id="paymentSummary.edit.title" defaultMessage="Edit Payment Summary" />
        </Heading.H3>
        {content}
      </FormLayout.Content>
    </FormLayout.Container>
  )
}

export default withLastUrl(EditPaymentSummaryView)
